import { ConnectDialog } from "@/components/android-instances/ConnectDialog";
import { StatusBadge } from "@/components/android-instances/StatusBadge";
import { formatDateTime, formatDuration } from "@/utils/format";
import {
  ArrowUpDown,
  ChevronDown,
  Layers,
  Plus,
  AlertCircle,
  Link2,
  Loader,
  ArrowUpRight,
  MoreHorizontal,
  Copy,
  RefreshCw,
  Terminal,
  Trash2,
  X,
} from "lucide-react";
import { FaApple, FaAndroid } from "react-icons/fa";
import { SiGradle, SiXcode } from "react-icons/si";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSWRConfig } from "swr";

import {
  useAndroidInstances,
  useGradleInstances,
  useIosInstances,
  useXcodeInstances,
} from "@/client/director/instances.tsx";
import {
  useInstanceConnections,
  useIosInstanceStates,
  type InstancePeer,
} from "@/client/director/instance-connections.ts";
import { platformFromInstanceId } from "@/lib/instance-platform.ts";
import type { Limrun } from "@limrun/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AppShell, PageHeader, EmptyState } from "@/components/layout/AppShell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/auth";
import { getLabeledIOSModel, isInternalInstanceLabel } from "@/lib/ios-model";

// Discriminated union type for combined instances
type CombinedInstance =
  | { platform: "android"; instance: Limrun.AndroidInstance }
  | { platform: "ios"; instance: Limrun.IosInstance }
  | { platform: "xcode"; instance: Limrun.XcodeInstance }
  | { platform: "gradle"; instance: Limrun.GradleInstance };

// SWR key prefixes of the four per-platform list hooks; must stay in sync
// with the hooks in client/director/instances.tsx.
const INSTANCE_LIST_SWR_KEYS = [
  "androidInstances",
  "iosInstances",
  "xcodeInstances",
  "gradleInstances",
] as const;

const INSTANCE_STATES = [
  { value: "creating", label: "Creating" },
  { value: "assigned", label: "Assigned" },
  { value: "ready", label: "Ready" },
  { value: "terminated", label: "Terminated" },
  { value: "unknown", label: "Unknown" },
] as const;

const ALL_INSTANCE_STATES: string[] = INSTANCE_STATES.map((s) => s.value);

// Terminated instances quickly outnumber live ones, so the list opens on the
// states a user can still act on; terminated is one checkbox away.
const ACTIVE_INSTANCE_STATES: string[] = ALL_INSTANCE_STATES.filter((s) => s !== "terminated");

function sameStates(a: string[], b: string[]) {
  return a.length === b.length && a.every((state) => b.includes(state));
}

type Platform = CombinedInstance["platform"];

const PLATFORMS: Platform[] = ["ios", "xcode", "android", "gradle"];

// "all" merges every platform into one list so paired instances (an iOS
// simulator attached to an Xcode instance) sit in the same table.
type TabValue = Platform | "all";

const PLATFORM_TABS: { value: TabValue; label: string }[] = [
  { value: "all", label: "All" },
  { value: "ios", label: "iOS" },
  { value: "xcode", label: "Xcode" },
  { value: "android", label: "Android" },
  { value: "gradle", label: "Android Build" },
];

function PlatformIcon({ platform, className }: { platform: TabValue; className?: string }) {
  switch (platform) {
    case "all":
      return <Layers aria-hidden className={className} />;
    case "ios":
      return <FaApple aria-hidden className={className} />;
    case "xcode":
      return <SiXcode aria-hidden className={className} />;
    case "gradle":
      return <SiGradle aria-hidden className={className} />;
    default:
      return <FaAndroid aria-hidden className={className} />;
  }
}

function getInstanceCreatedAtMs(instance: CombinedInstance["instance"]) {
  return new Date(instance.metadata.createdAt || 0).getTime();
}

function getInstanceDurationMs(instance: CombinedInstance["instance"]) {
  if (!instance.metadata.createdAt) {
    return 0;
  }

  const createdAt = new Date(instance.metadata.createdAt).getTime();
  const endAt = instance.metadata.terminatedAt
    ? new Date(instance.metadata.terminatedAt).getTime()
    : Date.now();

  return endAt - createdAt;
}

function getStreamPath(
  platform: CombinedInstance["platform"],
  instance: CombinedInstance["instance"],
): string {
  const model = platform === "ios" ? getLabeledIOSModel(instance.metadata.labels) : undefined;
  const params = new URLSearchParams();
  if (model) {
    params.set("model", model);
  }

  const query = params.toString();
  return `/stream/${instance.metadata.id}${query ? `?${query}` : ""}`;
}

export function Instances() {
  const auth = useAuth();
  const [stateFilter, setStateFilter] = useState<string[]>(() => {
    try {
      const saved = sessionStorage.getItem("instances:stateFilter");
      return saved ? JSON.parse(saved) : ACTIVE_INSTANCE_STATES;
    } catch {
      return ACTIVE_INSTANCE_STATES;
    }
  });
  useEffect(() => {
    sessionStorage.setItem("instances:stateFilter", JSON.stringify(stateFilter));
  }, [stateFilter]);
  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    const saved = sessionStorage.getItem("instances:platformTab");
    return PLATFORM_TABS.some((tab) => tab.value === saved)
      ? (saved as TabValue)
      : PLATFORM_TABS[0].value;
  });
  useEffect(() => {
    sessionStorage.setItem("instances:platformTab", activeTab);
  }, [activeTab]);
  const showsPlatform = useCallback(
    (platform: Platform) => activeTab === "all" || activeTab === platform,
    [activeTab],
  );
  const [sortBy, setSortBy] = useState<"createdAt" | "duration">("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [labelInput, setLabelInput] = useState("");
  const [labelFilters, setLabelFilters] = useState<string[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const labelSelector = labelFilters.join(",");
  const shouldShowTooltip = (label: string, maxChars: number) => label.length > maxChars;
  // Only the platforms shown by the active tab are fetched; the other hooks
  // are disabled so switching tabs is what triggers their (cached) fetch.
  const { data: androidData, error: androidError } = useAndroidInstances({
    states: stateFilter,
    labelSelector: labelSelector.length > 0 ? labelSelector : undefined,
    enabled: showsPlatform("android"),
  });
  const { data: iosData, error: iosError } = useIosInstances({
    states: stateFilter,
    labelSelector: labelSelector.length > 0 ? labelSelector : undefined,
    enabled: showsPlatform("ios"),
  });
  const { data: xcodeData, error: xcodeError } = useXcodeInstances({
    states: stateFilter,
    labelSelector: labelSelector.length > 0 ? labelSelector : undefined,
    enabled: showsPlatform("xcode"),
  });
  const { data: gradleData, error: gradleError } = useGradleInstances({
    states: stateFilter,
    labelSelector: labelSelector.length > 0 ? labelSelector : undefined,
    enabled: showsPlatform("gradle"),
  });

  if (androidError) {
    console.log({ androidError });
  }
  if (iosError) {
    console.log({ iosError });
  }
  if (xcodeError) {
    console.log({ xcodeError });
  }
  if (gradleError) {
    console.log({ gradleError });
  }

  // Rows per platform. Each platform owns its slice so that one list
  // revalidating (focus, delete, refresh) never drops rows another list
  // appended through loadMore.
  const [rowsByPlatform, setRowsByPlatform] = useState<Record<Platform, CombinedInstance[]>>({
    android: [],
    ios: [],
    xcode: [],
    gradle: [],
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const androidPageRef = useRef(androidData);
  const iosPageRef = useRef(iosData);
  const xcodePageRef = useRef(xcodeData);
  const gradlePageRef = useRef(gradleData);

  const sortInstances = useCallback(
    (instances: CombinedInstance[]) =>
      [...instances].sort((a, b) => {
        if (sortBy === "duration") {
          const durationDiff =
            getInstanceDurationMs(b.instance) - getInstanceDurationMs(a.instance);
          if (durationDiff !== 0) {
            return sortDirection === "desc" ? durationDiff : -durationDiff;
          }
        }

        const createdAtDiff =
          getInstanceCreatedAtMs(b.instance) - getInstanceCreatedAtMs(a.instance);
        return sortDirection === "desc" ? createdAtDiff : -createdAtDiff;
      }),
    [sortBy, sortDirection],
  );

  const handleSort = useCallback(
    (field: "createdAt" | "duration") => {
      if (sortBy === field) {
        setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
        return;
      }

      setSortBy(field);
      setSortDirection("desc");
    },
    [sortBy],
  );

  // A platform's slice restarts from its first page whenever that page
  // changes. "loading" (tab hidden, or a filter change awaiting its fetch)
  // empties the slice, so a freshly opened tab shows an empty table until
  // its fetch lands.
  useEffect(() => {
    androidPageRef.current = androidData;
    const rows: CombinedInstance[] =
      androidData === "loading"
        ? []
        : androidData
            .getPaginatedItems()
            .map((instance) => ({ platform: "android" as const, instance }));
    setRowsByPlatform((prev) => ({ ...prev, android: rows }));
  }, [androidData]);

  useEffect(() => {
    iosPageRef.current = iosData;
    const rows: CombinedInstance[] =
      iosData === "loading"
        ? []
        : iosData.getPaginatedItems().map((instance) => ({ platform: "ios" as const, instance }));
    setRowsByPlatform((prev) => ({ ...prev, ios: rows }));
  }, [iosData]);

  useEffect(() => {
    xcodePageRef.current = xcodeData;
    const rows: CombinedInstance[] =
      xcodeData === "loading"
        ? []
        : xcodeData
            .getPaginatedItems()
            .map((instance) => ({ platform: "xcode" as const, instance }));
    setRowsByPlatform((prev) => ({ ...prev, xcode: rows }));
  }, [xcodeData]);

  useEffect(() => {
    gradlePageRef.current = gradleData;
    const rows: CombinedInstance[] =
      gradleData === "loading"
        ? []
        : gradleData
            .getPaginatedItems()
            .map((instance) => ({ platform: "gradle" as const, instance }));
    setRowsByPlatform((prev) => ({ ...prev, gradle: rows }));
  }, [gradleData]);

  const allInstances = useMemo(
    () =>
      sortInstances(
        PLATFORMS.filter(showsPlatform).flatMap((platform) => rowsByPlatform[platform]),
      ),
    [rowsByPlatform, showsPlatform, sortInstances],
  );

  const loadMore = useCallback(async () => {
    if (isLoadingMore || isRefreshing) return;

    const androidPage = androidPageRef.current;
    const iosPage = iosPageRef.current;
    const xcodePage = xcodePageRef.current;
    const gradlePage = gradlePageRef.current;

    const hasAndroidMore =
      showsPlatform("android") && androidPage !== "loading" && androidPage.hasNextPage();
    const hasIosMore = showsPlatform("ios") && iosPage !== "loading" && iosPage.hasNextPage();
    const hasXcodeMore =
      showsPlatform("xcode") && xcodePage !== "loading" && xcodePage.hasNextPage();
    const hasGradleMore =
      showsPlatform("gradle") && gradlePage !== "loading" && gradlePage.hasNextPage();

    if (hasAndroidMore || hasIosMore || hasXcodeMore || hasGradleMore) {
      setIsLoadingMore(true);
      try {
        if (hasAndroidMore) {
          const nextPage = await androidPage.getNextPage();
          androidPageRef.current = nextPage;
          const rows = nextPage
            .getPaginatedItems()
            .map((instance) => ({ platform: "android" as const, instance }));
          setRowsByPlatform((prev) => ({ ...prev, android: [...prev.android, ...rows] }));
        }

        if (hasIosMore) {
          const nextPage = await iosPage.getNextPage();
          iosPageRef.current = nextPage;
          const rows = nextPage
            .getPaginatedItems()
            .map((instance) => ({ platform: "ios" as const, instance }));
          setRowsByPlatform((prev) => ({ ...prev, ios: [...prev.ios, ...rows] }));
        }

        if (hasXcodeMore) {
          const nextPage = await xcodePage.getNextPage();
          xcodePageRef.current = nextPage;
          const rows = nextPage
            .getPaginatedItems()
            .map((instance) => ({ platform: "xcode" as const, instance }));
          setRowsByPlatform((prev) => ({ ...prev, xcode: [...prev.xcode, ...rows] }));
        }

        if (hasGradleMore) {
          const nextPage = await gradlePage.getNextPage();
          gradlePageRef.current = nextPage;
          const rows = nextPage
            .getPaginatedItems()
            .map((instance) => ({ platform: "gradle" as const, instance }));
          setRowsByPlatform((prev) => ({ ...prev, gradle: [...prev.gradle, ...rows] }));
        }
      } catch (error) {
        console.error("Failed to load more instances:", error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [showsPlatform, isLoadingMore, isRefreshing]);

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Load more when scrolled 80% down
      if (scrollPercentage > 0.8) {
        loadMore();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadMore]);

  const [deletingInstances, setDeletingInstances] = useState<Set<string>>(new Set());

  const { mutate } = useSWRConfig();

  const handleDelete = async (instanceId: string) => {
    try {
      setActionError(undefined);
      setDeletingInstances((prev) => new Set(prev).add(instanceId));
      const platform = platformFromInstanceId(instanceId);
      await auth.regionClient[`${platform}Instances`].delete(instanceId);
      await mutate((key) => Array.isArray(key) && key[0] === `${platform}Instances`);
    } catch (error) {
      console.error("Failed to delete instance:", error);
      setActionError("Could not stop the instance. Try again.");
    } finally {
      setDeletingInstances((prev) => {
        const next = new Set(prev);
        next.delete(instanceId);
        return next;
      });
    }
  };

  const [connectDialog, setConnectDialog] = useState<{
    isOpen: boolean;
    instanceId?: string;
    apiUrl?: string;
    adbWebSocketUrl?: string;
    mcpUrl?: string;
    token?: string;
  }>({ isOpen: false });

  const addLabelFilters = useCallback((rawValue: string) => {
    const next = rawValue
      .split(",")
      .map((label) => label.trim())
      .filter(Boolean);

    if (next.length === 0) {
      return;
    }

    setLabelFilters((prev) => {
      const merged = new Set(prev);
      next.forEach((label) => merged.add(label));
      return Array.from(merged);
    });
    setLabelInput("");
  }, []);

  const toggleLabelFilter = useCallback((label: string) => {
    setLabelFilters((prev) =>
      prev.includes(label) ? prev.filter((existing) => existing !== label) : [...prev, label],
    );
  }, []);

  const toggleStateFilter = useCallback((state: string, checked: boolean) => {
    setStateFilter((prev) => {
      if (checked) {
        return INSTANCE_STATES.map((option) => option.value).filter(
          (value) => value === state || prev.includes(value),
        );
      }

      // Keep at least one state selected so the filter always has a valid value.
      if (prev.length === 1 && prev.includes(state)) {
        return prev;
      }

      return prev.filter((value) => value !== state);
    });
  }, []);

  const stateFilterLabel = sameStates(stateFilter, ALL_INSTANCE_STATES)
    ? "All states"
    : sameStates(stateFilter, ACTIVE_INSTANCE_STATES)
      ? "Active states"
      : stateFilter
          .map((value) => INSTANCE_STATES.find((option) => option.value === value)?.label ?? value)
          .join(", ");
  const hidesTerminated = !stateFilter.includes("terminated");

  const dataByPlatform = {
    android: androidData,
    ios: iosData,
    xcode: xcodeData,
    gradle: gradleData,
  };
  const pageRefs = {
    android: androidPageRef,
    ios: iosPageRef,
    xcode: xcodePageRef,
    gradle: gradlePageRef,
  };
  // The table counts as loading until every platform the tab shows has
  // its first page.
  const isTabLoading = PLATFORMS.filter(showsPlatform).some(
    (platform) => dataByPlatform[platform] === "loading",
  );

  const xcodeInstances = allInstances.flatMap((entry) =>
    entry.platform === "xcode" ? [entry.instance] : [],
  );
  const connections = useInstanceConnections(xcodeInstances);
  const loadedStates = useMemo(
    () =>
      new Map(
        allInstances.map((entry) => [entry.instance.metadata.id, entry.instance.status?.state]),
      ),
    [allInstances],
  );
  // iOS peers the table does not list (the Xcode tab, or a peer hidden by
  // the state filter) get their state fetched individually.
  const unlistedIosPeerIds = useMemo(
    () => [
      ...new Set(
        [...connections.values()]
          .flat()
          .filter((peer) => peer.platform === "ios" && !loadedStates.has(peer.id))
          .map((peer) => peer.id),
      ),
    ],
    [connections, loadedStates],
  );
  const unlistedIosPeerStates = useIosInstanceStates(unlistedIosPeerIds);
  // A peer's page: build logs for Xcode; for iOS the playground while it is
  // live, otherwise the session page, which also renders terminated
  // instances. A state still being fetched counts as live.
  const peerPath = (peer: InstancePeer) => {
    if (peer.platform === "xcode") return `/builds/${peer.id}`;
    const state = loadedStates.get(peer.id) ?? unlistedIosPeerStates.get(peer.id);
    return state === undefined || state === "ready" || state === "assigned"
      ? `/stream/${peer.id}`
      : `/sessions/ios/${peer.id}`;
  };

  const errorsByPlatform = {
    android: androidError,
    ios: iosError,
    xcode: xcodeError,
    gradle: gradleError,
  };
  const hasLoadError = PLATFORMS.filter(showsPlatform).some(
    (platform) => errorsByPlatform[platform],
  );

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    setActionError(undefined);
    try {
      // Revalidate in place so the table remains usable while requests finish.
      await mutate(
        (key) => Array.isArray(key) && INSTANCE_LIST_SWR_KEYS.some((prefix) => prefix === key[0]),
      );
    } catch (error) {
      console.error("Failed to refresh instances:", error);
      setActionError("Could not refresh instances. Try again.");
    } finally {
      setIsRefreshing(false);
    }
  }, [mutate]);

  return (
    <AppShell title="Instances">
      <PageHeader
        title="Instances"
        actions={
          <Button asChild>
            <Link to="/stream">
              <Plus aria-hidden />
              Create instance
            </Link>
          </Button>
        }
      />
      {(hasLoadError || actionError) && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-md border border-danger/30 bg-danger/5 px-4 py-3 text-sm"
        >
          <AlertCircle aria-hidden className="size-4 text-danger" />
          <span className="flex-1">
            {actionError ?? "Some instances could not be loaded. Your list may be incomplete."}
          </span>
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
            Retry
          </Button>
        </div>
      )}
      <Tabs
        className="flex min-w-0 flex-col gap-5"
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as TabValue)}
      >
        <TabsList
          aria-label="Instance platform"
          className="h-auto w-full flex-wrap justify-start gap-4 rounded-none border-b bg-transparent p-0 sm:gap-6"
        >
          {PLATFORM_TABS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="relative gap-2 rounded-none px-0 pb-3 pt-1 font-normal data-[state=active]:bg-transparent data-[state=active]:after:absolute data-[state=active]:after:inset-x-0 data-[state=active]:after:bottom-[-1px] data-[state=active]:after:h-px data-[state=active]:after:bg-primary"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={activeTab} className="mt-0 min-w-0">
          <section aria-label="Instance list" className="min-w-0">
            <div
              role="group"
              aria-label="Instance filters"
              className="flex flex-wrap items-center gap-2 pb-4"
            >
              {labelFilters.map((label) =>
                shouldShowTooltip(label, 24) ? (
                  <TooltipProvider key={label}>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-6 max-w-[220px] gap-1 overflow-hidden px-2 text-xs hover:bg-muted"
                          onClick={() => toggleLabelFilter(label)}
                        >
                          <span className="truncate">{label}</span>
                          <X className="h-3 w-3 text-muted-foreground" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{label}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    key={label}
                    variant="outline"
                    className="h-6 max-w-[220px] gap-1 overflow-hidden px-2 text-xs hover:bg-muted"
                    onClick={() => toggleLabelFilter(label)}
                  >
                    <span className="truncate">{label}</span>
                    <X className="h-3 w-3 text-muted-foreground" />
                  </Button>
                ),
              )}
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-8 max-w-full justify-between gap-2 px-2 text-muted-foreground"
                    >
                      <span className="truncate">{stateFilterLabel}</span>
                      <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel>Filter by state</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {INSTANCE_STATES.map((option) => (
                      <DropdownMenuCheckboxItem
                        key={option.value}
                        checked={stateFilter.includes(option.value)}
                        onCheckedChange={(checked) =>
                          toggleStateFilter(option.value, checked === true)
                        }
                        onSelect={(event) => event.preventDefault()}
                      >
                        {option.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      disabled={sameStates(stateFilter, ACTIVE_INSTANCE_STATES)}
                      onSelect={() => setStateFilter(ACTIVE_INSTANCE_STATES)}
                    >
                      Active states only
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={sameStates(stateFilter, ALL_INSTANCE_STATES)}
                      onSelect={() => setStateFilter(ALL_INSTANCE_STATES)}
                    >
                      Include terminated
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        aria-label="Refresh instances"
                      >
                        <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Refresh instances</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Input
                  value={labelInput}
                  onChange={(event) => setLabelInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === ",") {
                      event.preventDefault();
                      addLabelFilters(labelInput);
                    }
                  }}
                  onBlur={() => addLabelFilters(labelInput)}
                  placeholder="Filter by label…"
                  className="h-8 w-full border-border bg-transparent shadow-none sm:ml-auto sm:w-52"
                  aria-label="Filter instances by label selector"
                />
              </div>
            </div>
            <div aria-busy={isRefreshing || (isTabLoading && !hasLoadError)}>
              <span role="status" className="sr-only">
                {isRefreshing
                  ? "Refreshing instances"
                  : isTabLoading && !hasLoadError
                    ? "Loading instances"
                    : ""}
              </span>
              <Table
                aria-label="Instances"
                className="table-fixed [&_td]:px-2 [&_th]:px-2 [&_td:first-child]:pl-0 [&_th:first-child]:pl-0 [&_td:last-child]:pr-0 [&_th:last-child]:pr-0"
              >
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60%] md:w-[31%]">Instance</TableHead>
                    <TableHead className="hidden w-[20%] md:table-cell">Labels</TableHead>
                    <TableHead className="hidden w-[10%] md:table-cell">Status</TableHead>
                    <TableHead
                      className="hidden w-[10%] md:table-cell"
                      aria-sort={
                        sortBy === "duration"
                          ? sortDirection === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        className={cn(
                          "h-8 gap-1 px-0 text-sm text-muted-foreground hover:text-foreground",
                          sortBy === "duration" && "text-foreground",
                        )}
                        onClick={() => handleSort("duration")}
                        aria-label="Order by duration"
                      >
                        Duration
                        <ArrowUpDown
                          className={cn(
                            "h-3 w-3",
                            sortBy === "duration" && sortDirection === "asc" && "rotate-180",
                          )}
                        />
                      </Button>
                    </TableHead>
                    <TableHead
                      className="hidden w-[15%] md:table-cell"
                      aria-sort={
                        sortBy === "createdAt"
                          ? sortDirection === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        className={cn(
                          "h-8 gap-1 px-0 text-sm text-muted-foreground hover:text-foreground",
                          sortBy === "createdAt" && "text-foreground",
                        )}
                        onClick={() => handleSort("createdAt")}
                        aria-label="Order by created at"
                      >
                        Created at
                        <ArrowUpDown
                          className={cn(
                            "h-3 w-3",
                            sortBy === "createdAt" && sortDirection === "asc" && "rotate-180",
                          )}
                        />
                      </Button>
                    </TableHead>
                    <TableHead className="w-[40%] text-right md:w-[14%]">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allInstances.length === 0 &&
                    isTabLoading &&
                    !hasLoadError &&
                    Array.from({ length: 5 }, (_, index) => (
                      <TableRow key={index}>
                        <TableCell colSpan={6} className="h-16">
                          <Skeleton className="h-4 w-2/3 max-w-md" />
                        </TableCell>
                      </TableRow>
                    ))}
                  {allInstances.length === 0 && !isTabLoading && !hasLoadError && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={6}>
                        <EmptyState
                          icon={<Layers />}
                          title="No instances to show"
                          description={
                            hidesTerminated
                              ? "Create a cloud device or build environment, or include your terminated instances."
                              : "Try different filters or create an instance in the Playground."
                          }
                        >
                          <Button asChild size="sm">
                            <Link to="/stream">
                              <Plus aria-hidden />
                              Create instance
                            </Link>
                          </Button>
                          {hidesTerminated && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setStateFilter(ALL_INSTANCE_STATES)}
                            >
                              Include terminated
                            </Button>
                          )}
                        </EmptyState>
                      </TableCell>
                    </TableRow>
                  )}
                  {allInstances.map(({ platform, instance }) => (
                    <TableRow key={instance.metadata.id} className="border-border/60">
                      <TableCell className="font-semibold">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-3">
                            <PlatformIcon
                              platform={platform}
                              className="size-8 shrink-0 rounded-md border border-border/70 p-2 text-muted-foreground"
                            />
                            <div className="min-w-0">
                              <p
                                className="truncate"
                                title={instance.metadata.displayName || instance.metadata.id}
                              >
                                {instance.metadata.displayName || instance.metadata.id}
                              </p>
                              {instance.metadata.displayName && (
                                <p
                                  className="mt-0.5 truncate font-mono text-xs font-normal text-muted-foreground"
                                  title={instance.metadata.id}
                                >
                                  {PLATFORM_TABS.find((tab) => tab.value === platform)?.label} ·{" "}
                                  {instance.metadata.id.slice(-8)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="md:hidden">
                            <StatusBadge state={instance.status?.state ?? "unknown"} />
                          </div>
                          {(connections.get(instance.metadata.id) ?? []).map((peer) => (
                            <TooltipProvider key={peer.id}>
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <Link
                                    to={peerPath(peer)}
                                    className="inline-flex w-fit max-w-full items-center gap-1.5 rounded-full border bg-muted/40 px-2 py-0.5 text-xs font-normal text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                  >
                                    <Link2 className="h-3 w-3 shrink-0" />
                                    <PlatformIcon
                                      platform={peer.platform}
                                      className="h-3 w-3 shrink-0 text-muted-foreground"
                                    />
                                    <span className="truncate">{peer.id}</span>
                                  </Link>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {peer.platform === "xcode"
                                    ? "Attached to this Xcode instance. Open its build logs."
                                    : "This simulator is attached here. Open it."}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {(() => {
                          const visibleLabels = Object.entries(instance.metadata.labels ?? {})
                            .filter(([key]) => !isInternalInstanceLabel(key))
                            .sort(([a], [b]) => a.localeCompare(b));

                          if (visibleLabels.length === 0) {
                            return "—";
                          }

                          return (
                            <div className="flex flex-col items-start gap-1">
                              {visibleLabels.map(([key, value]) => {
                                const label = `${key}=${value}`;
                                const isActive = labelFilters.includes(label);
                                return shouldShowTooltip(label, 22) ? (
                                  <TooltipProvider key={label}>
                                    <Tooltip delayDuration={0}>
                                      <TooltipTrigger asChild>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant={isActive ? "secondary" : "ghost"}
                                          className={cn(
                                            "h-5 max-w-full overflow-hidden px-0 text-xs font-normal text-muted-foreground transition-colors",
                                            isActive ? "hover:bg-secondary/80" : "hover:bg-muted",
                                          )}
                                          onClick={() => toggleLabelFilter(label)}
                                        >
                                          <span className="truncate">{label}</span>
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>{label}</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <Button
                                    type="button"
                                    size="sm"
                                    key={label}
                                    variant={isActive ? "secondary" : "ghost"}
                                    className={cn(
                                      "h-5 max-w-full overflow-hidden px-0 text-xs font-normal text-muted-foreground transition-colors",
                                      isActive ? "hover:bg-secondary/80" : "hover:bg-muted",
                                    )}
                                    onClick={() => toggleLabelFilter(label)}
                                  >
                                    <span className="truncate">{label}</span>
                                  </Button>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <StatusBadge state={instance.status?.state ?? "unknown"} />
                      </TableCell>
                      <TableCell className="hidden text-xs md:table-cell">
                        {instance.metadata.createdAt
                          ? formatDuration(
                              (instance.metadata.terminatedAt
                                ? new Date(instance.metadata.terminatedAt).getTime()
                                : Date.now()) - new Date(instance.metadata.createdAt).getTime(),
                            )
                          : "N/A"}
                      </TableCell>
                      <TableCell className="hidden text-xs md:table-cell">
                        {instance.metadata.createdAt ? (
                          <time
                            dateTime={instance.metadata.createdAt}
                            title={formatDateTime(instance.metadata.createdAt)}
                            className="text-xs text-muted-foreground"
                          >
                            {new Date(instance.metadata.createdAt).toLocaleString(undefined, {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </time>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {(platform === "xcode" ||
                            platform === "gradle" ||
                            instance.status?.state === "ready" ||
                            instance.status?.state === "assigned" ||
                            instance.status?.state === "terminated") && (
                            <Button
                              asChild
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1 px-2 font-normal text-muted-foreground"
                            >
                              <Link
                                aria-label={
                                  platform === "xcode" || platform === "gradle"
                                    ? "Open build logs"
                                    : instance.status?.state === "terminated"
                                      ? "Open session"
                                      : "Open Playground"
                                }
                                to={
                                  platform === "xcode" || platform === "gradle"
                                    ? `/builds/${instance.metadata.id}`
                                    : instance.status?.state === "terminated"
                                      ? `/sessions/${platform}/${instance.metadata.id}`
                                      : getStreamPath(platform, instance)
                                }
                              >
                                Open <ArrowUpRight aria-hidden className="size-3.5" />
                              </Link>
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 text-muted-foreground"
                                aria-label={`Actions for ${instance.metadata.displayName || instance.metadata.id}`}
                              >
                                {deletingInstances.has(instance.metadata.id) ? (
                                  <Loader className="size-4 animate-spin" />
                                ) : (
                                  <MoreHorizontal className="size-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64">
                              <DropdownMenuLabel className="break-all font-mono text-[10px] font-normal text-muted-foreground">
                                {instance.metadata.id}
                              </DropdownMenuLabel>
                              <DropdownMenuItem
                                onSelect={() => {
                                  void navigator.clipboard.writeText(instance.metadata.id).then(
                                    () => toast({ title: "Instance ID copied" }),
                                    () =>
                                      setActionError("Could not copy the instance ID. Try again."),
                                  );
                                }}
                              >
                                <Copy className="mr-2 size-3.5" />
                                Copy instance ID
                              </DropdownMenuItem>
                              {(instance.status?.state === "ready" ||
                                instance.status?.state === "assigned") && (
                                <DropdownMenuItem
                                  onSelect={() =>
                                    setConnectDialog({
                                      isOpen: true,
                                      instanceId: instance.metadata.id,
                                      apiUrl: instance.status?.apiUrl,
                                      adbWebSocketUrl: (
                                        instance.status as { adbWebSocketUrl?: string }
                                      )?.adbWebSocketUrl,
                                      mcpUrl:
                                        platform === "xcode"
                                          ? undefined
                                          : (instance.status as { mcpUrl?: string })?.mcpUrl,
                                      token: instance.status?.token,
                                    })
                                  }
                                >
                                  <Terminal className="mr-2 size-3.5" />
                                  Connect locally
                                </DropdownMenuItem>
                              )}
                              {instance.status?.state !== "terminated" && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    disabled={deletingInstances.has(instance.metadata.id)}
                                    className="text-danger focus:text-danger"
                                    onSelect={() => void handleDelete(instance.metadata.id)}
                                  >
                                    <Trash2 className="mr-2 size-3.5" />
                                    {deletingInstances.has(instance.metadata.id)
                                      ? "Stopping instance…"
                                      : "Stop instance"}
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {!isTabLoading && (
              <footer className="flex flex-col items-start gap-2 border-t border-border/60 py-4">
                <div className="text-xs text-muted-foreground">
                  Showing <strong>{allInstances.length}</strong>
                  {hidesTerminated ? " active" : ""} instances
                  {PLATFORMS.filter(showsPlatform).some((platform) => {
                    const page = pageRefs[platform].current;
                    return page !== "loading" && page.hasNextPage();
                  }) && <span> (scroll down for more)</span>}
                </div>
                {isLoadingMore && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader className="h-3 w-3 animate-spin" />
                    Loading more instances...
                  </div>
                )}
              </footer>
            )}
          </section>
        </TabsContent>
      </Tabs>
      {connectDialog.instanceId && (
        <ConnectDialog
          isOpen={connectDialog.isOpen}
          instanceId={connectDialog.instanceId}
          apiUrl={connectDialog.apiUrl}
          adbWebSocketUrl={connectDialog.adbWebSocketUrl}
          mcpUrl={connectDialog.mcpUrl}
          token={connectDialog.token}
          onOpenChange={(isOpen) => setConnectDialog({ isOpen })}
        />
      )}
    </AppShell>
  );
}
